<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import { useMapGetter } from 'dashboard/composables/store';
import DealCard from 'dashboard/components-next/Deal/DealCard.vue';
import DealEditor from 'dashboard/components-next/Deal/DealEditor.vue';
import DealWonLostDialog from 'dashboard/components-next/Deal/DealWonLostDialog.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { funnels, loadFunnels } = useFunnelManager();

// Estado local
const mappings = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showAddForm = ref(false);
const selectedFunnelId = ref(null);
const selectedStage = ref(null);

// Estado do Deal
const editingDealMappingId = ref(null);
const isDealSaving = ref(false);
const showWonLostDialog = ref(false);
const wonLostAction = ref('won');
const wonLostMappingId = ref(null);
const wonLostDealValue = ref(0);
const wonLostDealCurrency = ref('BRL');

// Use o getter de chat selecionado
const currentChat = useMapGetter('getSelectedChat');

// Computed para acessar a conversa atual
const currentConversation = computed(() => {
  if (currentChat.value?.id === Number(props.conversationId)) {
    return currentChat.value;
  }
  return { id: props.conversationId };
});

// Considera funis ativos: explicitamente active=true OU sem campo active definido (undefined/null)
const activeFunnels = computed(() => funnels.value.filter(f => f.active !== false));

const availableFunnels = computed(() => {
  // Funis que ainda não estão mapeados para esta conversa
  const mappedFunnelIds = mappings.value.map(m => m.funnel_id);
  return activeFunnels.value.filter(f => !mappedFunnelIds.includes(f.id));
});

const selectedFunnel = computed(() =>
  funnels.value.find(f => f.id === selectedFunnelId.value)
);

const availableStages = computed(() => {
  if (!selectedFunnel.value || !selectedFunnel.value.stages) {
    return [];
  }

  return Object.entries(selectedFunnel.value.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([key, stage]) => ({
      key,
      label: stage.label,
      color: stage.color,
    }));
});

// Methods
const loadMappings = async () => {
  isLoading.value = true;
  try {
    const response = await ConversationFunnelMappingsAPI.getMappings(
      props.conversationId
    );
    // Compatibilidade com ambos os formatos de resposta
    if (Array.isArray(response.data)) {
      mappings.value = response.data;
    } else if (response.data && Array.isArray(response.data.mappings)) {
      mappings.value = response.data.mappings;
    } else {
      mappings.value = [];
    }
  } catch (error) {
    // Silently handle canceled requests (user navigated away)
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      return;
    }
    // eslint-disable-next-line no-console
    console.error('Erro ao carregar mapeamentos:', error);
    mappings.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleFunnelChange = () => {
  // Resetar a etapa quando mudar o funil
  selectedStage.value = null;

  // Só definir etapa padrão se explicitamente configurada
  if (selectedFunnel.value && selectedFunnel.value.settings?.default_stage) {
    selectedStage.value = selectedFunnel.value.settings.default_stage;
  }
};

const addToFunnel = async () => {
  if (!selectedFunnelId.value || !selectedStage.value) {
    useAlert(t('MULTIPLE_FUNNELS.MESSAGES.SELECT_FUNNEL_STAGE'));
    return;
  }

  isSaving.value = true;

  // Guardar valores antes de limpar
  const funnelId = selectedFunnelId.value;
  const stageName = selectedStage.value;

  try {
    const response = await ConversationFunnelMappingsAPI.addToFunnel(
      props.conversationId,
      funnelId,
      stageName
    );

    if (response.data.success) {
      // Adicionar o novo mapeamento à lista local
      mappings.value.push(response.data.mapping);

      // Atualizar conversa no store Vuex
      if (currentConversation.value) {
        const updatedConversation = {
          ...currentConversation.value,
          funnel_mappings: [
            ...(currentConversation.value.funnel_mappings || []),
            response.data.mapping,
          ],
          custom_attributes: {
            ...currentConversation.value.custom_attributes,
            funnel_id: funnelId.toString(),
            stage_name: stageName,
          },
        };
        store.dispatch('updateConversation', updatedConversation);
      }

      // Emitir evento para o KanbanView atualizar ANTES de limpar valores
      window.dispatchEvent(
        new CustomEvent('conversation-added-to-funnel', {
          detail: {
            conversationId: props.conversationId,
            funnelId: funnelId,
            stageName: stageName,
            mapping: response.data.mapping,
            conversation: currentConversation.value,
          },
        })
      );

      // Limpar seleção e fechar formulário
      selectedFunnelId.value = null;
      selectedStage.value = null;
      showAddForm.value = false;

      useAlert(t('MULTIPLE_FUNNELS.MESSAGES.ADD_SUCCESS'));
    }
  } catch (error) {
    console.error('Erro ao adicionar conversa ao funil:', error);
    useAlert(t('MULTIPLE_FUNNELS.MESSAGES.ADD_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const removeFromFunnel = async mapping => {
  if (
    !confirm(
      `🗑️ Remover do Funil\n\nTem certeza que deseja remover esta conversa do funil "${mapping.funnel_name}"?\n\nA conversa continuará existindo, apenas será removida deste funil.`
    )
  ) {
    return;
  }

  try {
    await ConversationFunnelMappingsAPI.removeFromFunnel(
      props.conversationId,
      mapping.id
    );

    // Remover da lista local
    const index = mappings.value.findIndex(m => m.id === mapping.id);
    if (index !== -1) {
      mappings.value.splice(index, 1);
    }

    useAlert(`Conversa removida do funil "${mapping.funnel_name}"`);

    // Emitir evento para o KanbanView atualizar
    window.dispatchEvent(
      new CustomEvent('conversation-removed-from-funnel', {
        detail: {
          conversationId: props.conversationId,
          funnelId: mapping.funnel_id,
        },
      })
    );
  } catch (error) {
    console.error('Erro ao remover conversa do funil:', error);
    useAlert('Erro ao remover conversa do funil. Tente novamente.');
  }
};

const moveToStage = async (mapping, newStage) => {
  try {
    const response = await ConversationFunnelMappingsAPI.moveToStage(
      props.conversationId,
      mapping.id,
      newStage
    );

    if (response.data.success) {
      // Atualizar o mapeamento local
      const index = mappings.value.findIndex(m => m.id === mapping.id);
      if (index !== -1) {
        mappings.value[index] = response.data.mapping;
      }

      // Atualizar conversa no store Vuex se for o primeiro funil
      if (currentConversation.value && index === 0) {
        const updatedConversation = {
          ...currentConversation.value,
          custom_attributes: {
            ...currentConversation.value.custom_attributes,
            funnel_id: mapping.funnel_id.toString(),
            stage_name: newStage,
          },
        };

        // Atualizar funnel_mappings
        if (currentConversation.value.funnel_mappings) {
          const mappingIndex =
            currentConversation.value.funnel_mappings.findIndex(
              m => m.id === mapping.id
            );
          if (mappingIndex !== -1) {
            updatedConversation.funnel_mappings = [
              ...currentConversation.value.funnel_mappings,
            ];
            updatedConversation.funnel_mappings[mappingIndex] =
              response.data.mapping;
          }
        }

        store.dispatch('updateConversation', updatedConversation);
      }

      // Emitir evento para o KanbanView atualizar
      window.dispatchEvent(
        new CustomEvent('funnel-mapping-updated', {
          detail: {
            conversation_id: props.conversationId,
            funnel_id: mapping.funnel_id,
            stage_name: newStage,
            stage_label: response.data.mapping.stage_label,
            stage_color: response.data.mapping.stage_color,
            mapping: response.data.mapping,
          },
        })
      );

      useAlert(`Conversa movida para "${response.data.mapping.stage_label}"`);
    }
  } catch (error) {
    console.error('Erro ao mover conversa:', error);
    useAlert('Erro ao mover conversa. Tente novamente.');
  }
};

const goToKanbanWithFunnel = funnelId => {
  const accountId = store.getters.getCurrentAccountId;

  // Armazenar o ID da conversa para abrir o modal automaticamente
  localStorage.setItem('kanban_open_conversation', props.conversationId);

  // Navegar para o Kanban com o funil específico
  if (router && router.push) {
    router.push({
      name: 'kanban_funnel_view',
      params: {
        accountId: accountId,
        funnelId: funnelId,
      },
    });
  }
};

const getFunnelStages = funnelId => {
  const funnel = funnels.value.find(f => f.id === funnelId);
  if (!funnel || !funnel.stages) return [];

  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([key, stage]) => ({
      key,
      label: stage.label,
      color: stage.color,
    }));
};

const formatTimeInStage = timeInSeconds => {
  if (timeInSeconds < 60) {
    return `${Math.round(timeInSeconds)}s`;
  }
  if (timeInSeconds < 3600) {
    return `${Math.round(timeInSeconds / 60)}min`;
  }
  if (timeInSeconds < 86400) {
    return `${Math.round(timeInSeconds / 3600)}h`;
  }
  return `${Math.round(timeInSeconds / 86400)}d`;
};

const cancelAddForm = () => {
  selectedFunnelId.value = null;
  selectedStage.value = null;
  showAddForm.value = false;
};

// Formatar mapping para o DealCard/DealEditor (snake_case para camelCase)
const formatMappingForDeal = mapping => {
  return {
    id: mapping.id,
    dealValue: mapping.deal_value,
    dealCurrency: mapping.deal_currency || 'BRL',
    probability: mapping.probability,
    expectedCloseDate: mapping.expected_close_date,
    weightedValue:
      mapping.deal_value && mapping.probability
        ? (mapping.deal_value * mapping.probability) / 100
        : null,
    dealStatus: mapping.deal_status || 'deal_open',
    wonAt: mapping.won_at,
    lostAt: mapping.lost_at,
    daysUntilClose: mapping.days_until_close,
    isOverdue: mapping.is_overdue,
  };
};

// Abrir editor de deal
const openDealEditor = mappingId => {
  editingDealMappingId.value = mappingId;
};

// Cancelar edição do deal
const cancelDealEditor = () => {
  editingDealMappingId.value = null;
};

// Salvar deal
const saveDeal = async (mappingId, dealData) => {
  isDealSaving.value = true;
  try {
    const response = await ConversationFunnelMappingsAPI.updateDeal(
      props.conversationId,
      mappingId,
      dealData
    );

    if (response.data.success) {
      // Atualizar o mapping local
      const index = mappings.value.findIndex(m => m.id === mappingId);
      if (index !== -1) {
        mappings.value[index] = response.data.mapping;
      }
      editingDealMappingId.value = null;
      useAlert(t('CRM.DEAL.SAVED'));
    }
  } catch (error) {
    console.error('Erro ao salvar deal:', error);
    useAlert(t('CRM.DEAL.SAVE_ERROR'));
  } finally {
    isDealSaving.value = false;
  }
};

// Abrir dialog de Won
const openWonDialog = mapping => {
  wonLostAction.value = 'won';
  wonLostMappingId.value = mapping.id;
  wonLostDealValue.value = mapping.deal_value || 0;
  wonLostDealCurrency.value = mapping.deal_currency || 'BRL';
  showWonLostDialog.value = true;
};

// Abrir dialog de Lost
const openLostDialog = mapping => {
  wonLostAction.value = 'lost';
  wonLostMappingId.value = mapping.id;
  wonLostDealValue.value = mapping.deal_value || 0;
  wonLostDealCurrency.value = mapping.deal_currency || 'BRL';
  showWonLostDialog.value = true;
};

// Confirmar Won/Lost
const confirmWonLost = async reason => {
  isDealSaving.value = true;
  try {
    const apiMethod =
      wonLostAction.value === 'won'
        ? ConversationFunnelMappingsAPI.markAsWon
        : ConversationFunnelMappingsAPI.markAsLost;

    const response = await apiMethod(
      props.conversationId,
      wonLostMappingId.value,
      reason
    );

    if (response.data.success) {
      // Atualizar o mapping local
      const index = mappings.value.findIndex(
        m => m.id === wonLostMappingId.value
      );
      if (index !== -1) {
        mappings.value[index] = response.data.mapping;
      }
      showWonLostDialog.value = false;
      useAlert(
        wonLostAction.value === 'won'
          ? t('CRM.DEAL.MARKED_WON')
          : t('CRM.DEAL.MARKED_LOST')
      );
    }
  } catch (error) {
    console.error('Erro ao atualizar status do deal:', error);
    useAlert(t('CRM.DEAL.STATUS_ERROR'));
  } finally {
    isDealSaving.value = false;
  }
};

// Cancelar dialog Won/Lost
const cancelWonLost = () => {
  showWonLostDialog.value = false;
  wonLostMappingId.value = null;
};

// Reabrir deal
const reopenDeal = async mapping => {
  isDealSaving.value = true;
  try {
    const response = await ConversationFunnelMappingsAPI.reopenDeal(
      props.conversationId,
      mapping.id
    );

    if (response.data.success) {
      // Atualizar o mapping local
      const index = mappings.value.findIndex(m => m.id === mapping.id);
      if (index !== -1) {
        mappings.value[index] = response.data.mapping;
      }
      useAlert(t('CRM.DEAL.REOPENED'));
    }
  } catch (error) {
    console.error('Erro ao reabrir deal:', error);
    useAlert(t('CRM.DEAL.REOPEN_ERROR'));
  } finally {
    isDealSaving.value = false;
  }
};

// Lifecycle
onMounted(async () => {
  await loadFunnels();
  // Debug: verificar funis carregados
  // eslint-disable-next-line no-console
  console.log('[ConversationFunnelMultiple] Funis carregados:', {
    total: funnels.value.length,
    funnels: funnels.value,
    activeFunnels: activeFunnels.value,
    availableFunnels: availableFunnels.value,
  });
  await loadMappings();
});

// Watch para recarregar quando a conversa mudar
watch(
  () => props.conversationId,
  () => {
    loadMappings();
  }
);
</script>

<template>
  <div class="conversation-funnel-multiple p-4">
    <div v-if="isLoading" class="flex justify-center py-4">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-woot-500"
      />
    </div>

    <div v-else class="space-y-4">
      <!-- Lista de Funis Atuais -->
      <div v-if="mappings.length > 0" class="space-y-3">
        <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
          Funis Ativos ({{ mappings.length }})
        </h4>

        <div
          v-for="mapping in mappings"
          :key="mapping.id"
          class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3"
        >
          <!-- Header do Funil -->
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <span class="text-lg">
                {{
                  funnels.find(f => f.id === mapping.funnel_id)?.settings
                    ?.icon || '📊'
                }}
              </span>
              <h5 class="font-medium text-slate-900 dark:text-slate-100">
                {{ mapping.funnel_name }}
              </h5>
            </div>

            <div class="flex items-center gap-2">
              <!-- Botão para ir ao Kanban -->
              <button
                class="p-1.5 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                title="Abrir no Kanban"
                @click="goToKanbanWithFunnel(mapping.funnel_id)"
              >
                <fluent-icon icon="open" size="16" />
              </button>

              <!-- Botão para remover com ícone de lixeira -->
              <button
                class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all rounded group"
                title="Remover conversa deste funil"
                @click="removeFromFunnel(mapping)"
              >
                <fluent-icon icon="delete" size="16" />
              </button>
            </div>
          </div>

          <!-- Etapa Atual -->
          <div class="mb-2">
            <span
              :style="{
                backgroundColor: mapping.stage_color + '20',
                color: mapping.stage_color,
              }"
              class="inline-block px-2 py-1 rounded-full text-xs font-medium"
            >
              {{ mapping.stage_label }}
            </span>

            <!-- Tempo na etapa -->
            <span class="ml-2 text-xs text-slate-500 dark:text-slate-400">
              {{ formatTimeInStage(mapping.time_in_stage) }} nesta etapa
            </span>

            <!-- Indicador SLA -->
            <span
              v-if="mapping.sla_breached"
              class="ml-2 inline-block px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
            >
              SLA Violado
            </span>
          </div>

          <!-- Seletor de Nova Etapa -->
          <div class="flex items-center gap-2">
            <select
              :value="mapping.stage_name"
              class="flex-1 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded focus:ring-1 focus:ring-woot-500 focus:border-woot-500"
              @change="moveToStage(mapping, $event.target.value)"
            >
              <option
                v-for="stage in getFunnelStages(mapping.funnel_id)"
                :key="stage.key"
                :value="stage.key"
                :selected="stage.key === mapping.stage_name"
              >
                {{ stage.label }}
              </option>
            </select>
          </div>

          <!-- Deal Section -->
          <div
            class="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700"
          >
            <!-- Deal Editor (editando) -->
            <DealEditor
              v-if="editingDealMappingId === mapping.id"
              :mapping="formatMappingForDeal(mapping)"
              :is-loading="isDealSaving"
              @save="dealData => saveDeal(mapping.id, dealData)"
              @cancel="cancelDealEditor"
            />

            <!-- Deal Card ou botão de adicionar -->
            <template v-else>
              <DealCard
                v-if="mapping.deal_value"
                :mapping="formatMappingForDeal(mapping)"
                :compact="true"
                @edit="openDealEditor(mapping.id)"
                @mark-won="openWonDialog(mapping)"
                @mark-lost="openLostDialog(mapping)"
                @reopen="reopenDeal(mapping)"
              />
              <button
                v-else
                class="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-500 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-woot-500 hover:text-woot-500 transition-colors"
                @click="openDealEditor(mapping.id)"
              >
                <fluent-icon icon="currency-dollar-rupee" size="14" />
                <span>{{ t('CRM.DEAL.ADD_VALUE') }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Estado Vazio -->
      <div v-else class="text-center py-6 text-slate-500 dark:text-slate-400">
        <fluent-icon icon="tray" size="32" class="mb-2" />
        <p class="text-sm">Esta conversa não está em nenhum funil</p>
        <p class="text-xs">Adicione abaixo para começar o acompanhamento</p>
      </div>

      <!-- Botão para Adicionar Novo Funil -->
      <div class="border-t border-slate-200 dark:border-slate-700 pt-4">
        <button
          v-if="!showAddForm && availableFunnels.length > 0"
          class="w-full px-4 py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 rounded-lg hover:border-woot-500 hover:text-woot-500 transition-colors flex items-center justify-center gap-2"
          @click="showAddForm = true"
        >
          <fluent-icon icon="add" size="16" />
          <span>Adicionar a outro funil</span>
        </button>

        <!-- Formulário de Adição -->
        <div v-if="showAddForm" class="space-y-3">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Selecionar Funil
            </label>
            <select
              v-model="selectedFunnelId"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-woot-500 focus:border-woot-500"
              :disabled="isSaving"
              @change="handleFunnelChange"
            >
              <option :value="null">Selecione um funil</option>
              <option
                v-for="funnel in availableFunnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.settings?.icon || '📊' }} {{ funnel.name }}
              </option>
            </select>
          </div>

          <div v-if="selectedFunnelId">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Selecionar Etapa
            </label>
            <select
              v-model="selectedStage"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-woot-500 focus:border-woot-500"
              :disabled="isSaving"
            >
              <option :value="null">Selecione uma etapa</option>
              <option
                v-for="stage in availableStages"
                :key="stage.key"
                :value="stage.key"
              >
                {{ stage.label }}
              </option>
            </select>
          </div>

          <!-- Botões de Ação do Formulário -->
          <div class="flex gap-2">
            <button
              v-if="selectedFunnelId && selectedStage"
              :disabled="isSaving"
              class="flex-1 px-4 py-2 bg-woot-500 text-white rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              @click="addToFunnel"
            >
              <span v-if="isSaving">Adicionando...</span>
              <span v-else>Adicionar ao Funil</span>
            </button>

            <button
              :disabled="isSaving"
              class="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              @click="cancelAddForm"
            >
              Cancelar
            </button>
          </div>
        </div>

        <!-- Mensagem quando não há funis disponíveis -->
        <div
          v-if="
            !showAddForm &&
            availableFunnels.length === 0 &&
            activeFunnels.length > 0
          "
          class="text-center text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1"
        >
          <fluent-icon icon="info" size="14" />
          <span>Esta conversa já está em todos os funis disponíveis</span>
        </div>

        <!-- Mensagem quando não há funis criados -->
        <div
          v-if="activeFunnels.length === 0"
          class="text-center text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1"
        >
          <fluent-icon icon="info" size="14" />
          <span>Nenhum funil ativo encontrado. Crie um funil primeiro.</span>
        </div>
      </div>
    </div>

    <!-- Deal Won/Lost Dialog -->
    <DealWonLostDialog
      :is-open="showWonLostDialog"
      :type="wonLostAction"
      :deal-value="wonLostDealValue"
      :deal-currency="wonLostDealCurrency"
      :is-loading="isDealSaving"
      @confirm="confirmWonLost"
      @cancel="cancelWonLost"
    />
  </div>
</template>

<style scoped>
.conversation-funnel-multiple {
  min-height: 200px;
}

/* Animação para os cards de funil */
.bg-slate-50 {
  transition: all 0.2s ease-in-out;
}

.bg-slate-50:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Estilo para o botão de adicionar */
.border-dashed {
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 5px,
    currentColor 5px,
    currentColor 10px
  );
  background-size: 100% 1px;
  background-repeat: repeat-x;
  background-position: 0 50%;
}
</style>
